import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [
    { title: "刑事事件 - 捜査・保釈・裁判の専門弁護士 | 法務法人フィサン" },
    { name: "description", content: "韓国ソウルの刑事事件専門弁護士。警察・検察の捜査段階から裁判まで一貫サポート。保釈申請、被害者支援、日本語対応で安心。軽微な事件から重大犯罪まで幅広く対応。" },
    { name: "keywords", content: "刑事事件, 刑事弁護, 保釈申請, 逮捕, 勾留, 被害者支援, 捜査対応, 刑事裁判, 韓国弁護士, 日本語対応, ソウル法律事務所" },
    { property: "og:title", content: "刑事事件 - 捜査・保釈・裁判の専門弁護士 | 法務法人フィサン" },
    { property: "og:description", content: "警察・検察の捜査段階から裁判まで一貫サポート。保釈申請、被害者支援など刑事事件の専門的な弁護サービスを日本語で提供します。" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://jp-hwisang.co.kr/ja/services/criminal" },
    { property: "og:image", content: "https://jp-hwisang.co.kr/images/jp-hwisang-card.jpg" },
    { property: "og:locale", content: "ja_JP" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "robots", content: "index, follow" },
  ];
};

export default function CriminalDetail() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <a href="/ja" className="flex items-center justify-center md:justify-start">
              <img src="/images/no-padding-logo.png" alt="法務法人フィサン" className="h-8 w-auto opacity-90" />
            </a>

            <div className="flex items-center justify-center md:justify-end gap-4 md:gap-6">
              <nav className="flex items-center gap-4 md:gap-6 text-sm">
                <a href="/ja/office" className="text-gray-700 hover:text-black whitespace-nowrap">事務所・所在地</a>
                <a href="/ja/services" className="text-black font-medium whitespace-nowrap">業務分野</a>
                <a href="/ja/blog" className="text-gray-700 hover:text-black whitespace-nowrap">法律ブログ</a>
              </nav>
              <a
                href="https://jp-hwisang.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 border border-gray-300 hover:border-gray-900 text-gray-700 hover:text-black text-sm transition-colors whitespace-nowrap"
              >
                한국어
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <section className="py-8 px-4 border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <a href="/ja/services" className="inline-flex items-center text-sm text-gray-600 hover:text-black transition-colors">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            業務分野一覧に戻る
          </a>
        </div>
      </section>

      {/* Title Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-serif mb-6 text-gray-900">刑事事件</h1>
          <div className="w-16 h-px bg-gray-400 mb-8"></div>
          <p className="text-xl text-gray-700 leading-relaxed">
            刑事事件 ― 刑事専門の弁護士が、お客様の権利を守ります
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto space-y-12">
          <div>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              予期せぬ刑事事件に巻き込まれることは、誰にとっても大きな不安とストレスを伴うものです。言葉の壁や制度の違いがある韓国では、より一層その不安が大きくなることでしょう。
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              輝相（フィサン）は、軽微な事件から重大犯罪まで、幅広い刑事事件に対応しています。経験豊富な弁護士が、事実関係を正確に把握し、最善の結果を導くための戦略を立てます。
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              単なる弁護活動にとどまらず、依頼者の心理的な負担にも寄り添いながら、誠実かつ冷静に対応いたします。輝相（フィサン）は、刑事事件の専門弁護士として、あなたの権利と名誉を守るために全力を尽くします。
            </p>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">捜査段階から裁判まで、一貫したサポート</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                刑事事件では、初期対応の速さと正確さが結果を大きく左右します。私たちは、警察・検察による捜査段階から、裁判手続きに至るまで、一貫してお客様をサポートし、最も有利な解決を目指します。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">保釈・逮捕関連の法的アドバイス</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                「勾留期間」や「保釈申請」の手続きには専門的な知識が不可欠です。私たちは、保釈や逮捕・拘留に関する迅速な法的助言を行い、不当な拘束を防ぎ、依頼者の自由と尊厳を守ります。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">犯罪被害者のための法的支援</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                輝相（フィサン）は、被疑者の弁護だけでなく、犯罪被害者の代理人としての活動も行っています。被害届や告訴の代理、損害賠償請求の手続きなど、被害者の立場から正当な権利を守るためのサポートを提供します。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">信頼できるパートナーとして</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                刑事事件は、単なる法的手続きではなく、人生に深く関わる問題です。私たちは、日本人のお客様が安心して韓国で法的対応を受けられるよう、言語と文化の両面からサポートいたします。
              </p>
              <p>
                どのような状況でも、「あなたの味方」として、最善の解決を目指します。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            刑事事件でお困りの方は、<br />お気軽にご相談ください。
          </p>
          <div className="space-y-4">
            <a href="tel:010-4729-3573" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">010-4729-3573</p>
            </a>
            <a href="mailto:mgkim@hwisang.co.kr" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">mgkim@hwisang.co.kr</p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">法務法人フィサン</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                韓国を代表する法律事務所として、<br />
                専門的で信頼できる法律サービスを提供します
              </p>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">業務分野</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>刑事事件</li>
                <li>金融・不動産</li>
                <li>経営権紛争</li>
                <li>家事事件</li>
              </ul>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">お問い合わせ</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>電話: 010-4729-3573</li>
                <li>Email: mgkim@hwisang.co.kr</li>
                <li>住所: ソウル特別市江南区永東大路611, 13階</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-8 text-center">
            <p className="text-xs text-gray-500">&copy; 2024 法務法人フィサン. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
